import { zodResolver } from '@hookform/resolvers/zod';
import { Globe } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateCountry } from '../hooks/useCreateCountry';
import {
  createCountrySchema,
  TERRITORIAL_LEVELS,
  type CreateCountryFormValues,
} from '../schemas/country-schema';
import {
  CURRENCIES,
  TerritorialLevel,
  TerritorialLevelNumber,
  type CreateCountryRequest,
} from '../types/countries.types';
import { CountryHierarchyInput } from './CountryHierarchyInput';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import { FormControl, FormField, FormItem, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

interface CreateCountryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LEVEL_LABELS: Record<string, string> = {
  [TerritorialLevel.Pais]: 'País',
  [TerritorialLevel.Estado]: 'Estado / Provincia',
  [TerritorialLevel.Municipio]: 'Municipio',
  [TerritorialLevel.Localidad]: 'Localidad',
};

export function CreateCountryModal({ open, onOpenChange }: CreateCountryModalProps) {
  const { createCountry, isCreating } = useCreateCountry();
  const [pendingValues, setPendingValues] = useState<CreateCountryFormValues | null>(null);

  const form = useForm<CreateCountryFormValues>({
    resolver: zodResolver(createCountrySchema),
    defaultValues: {
      officialName: '',
      commonName: '',
      isoCode: '',
      currencyCode: '',
      level: TerritorialLevel.Pais,
      parentId: '',
    },
  });

  const selectedLevel = form.watch('level');

  const onSubmit = (values: CreateCountryFormValues) => {
    setPendingValues(values);
  };

  const handleConfirm = () => {
    if (!pendingValues) return;
    const payload: CreateCountryRequest = {
      ...pendingValues,
      level: TerritorialLevelNumber[pendingValues.level],
      parentId: pendingValues.level === TerritorialLevel.Pais ? '' : pendingValues.parentId,
    };
    createCountry(payload, {
      onSuccess: () => {
        Notification.success({ title: 'Territorio registrado exitosamente' });
        setPendingValues(null);
        form.reset();
        onOpenChange(false);
      },
      onError: () => {
        setPendingValues(null);
      },
    });
  };

  const handleClose = () => {
    if (!isCreating) {
      form.reset();
      onOpenChange(false);
    }
  };

  const handleLevelChange = (value: string) => {
    form.setValue('level', value as TerritorialLevel);
    form.setValue('parentId', '');
    if (value !== TerritorialLevel.Pais) {
      form.setValue('isoCode', '');
    }
  };

  return (
    <>
      <ModalForm
        open={open}
        headerIcon={<Globe />}
        onOpenChange={handleClose}
        title="Registrar territorio"
        description="Configura un nuevo territorio en el sistema."
        className="sm:max-w-[425px]"
        data-testid="create-country-modal"
        form={form}
        onSubmit={onSubmit}
        footer={
          <ModalFooter>
            <div className="flex flex-row justify-end w-full gap-4">
              <UnifiedButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isCreating}
                data-testid="cancel-create-country-btn"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                variant="outline"
                actionType="submit"
                disabled={isCreating}
                data-testid="save-country-btn"
              >
                Guardar
              </UnifiedButton>
            </div>
          </ModalFooter>
        }
      >
        <FormField
          control={form.control}
          name="level"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <SelectInput
                  label="Nivel territorial"
                  value={field.value}
                  onValueChange={handleLevelChange}
                  disabled={isCreating}
                  placeholder="Seleccionar nivel"
                  error={form.formState.errors.level?.message}
                  data-testid="country-level-select"
                >
                  {TERRITORIAL_LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>
                      {LEVEL_LABELS[level] ?? level}
                    </SelectItem>
                  ))}
                </SelectInput>
              </FormControl>
            </FormItem>
          )}
        />

        {selectedLevel && selectedLevel !== TerritorialLevel.Pais && (
          <FormField
            control={form.control}
            name="parentId"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <CountryHierarchyInput
                    key={selectedLevel}
                    value={value || null}
                    onSelect={(id) => onChange(id ?? '')}
                    label="Territorio padre"
                    placeholder="Seleccionar territorio padre"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="officialName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  label="Nombre oficial"
                  placeholder="Ej: Colombia"
                  data-testid="country-official-name-input"
                  disabled={isCreating}
                  error={form.formState.errors.officialName?.message}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="commonName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  label="Nombre común / alias"
                  placeholder="Ej: Colombia"
                  data-testid="country-common-name-input"
                  disabled={isCreating}
                  error={form.formState.errors.commonName?.message}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {selectedLevel === TerritorialLevel.Pais && (
          <FormField
            control={form.control}
            name="isoCode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Código ISO"
                    placeholder="Ej: CO"
                    data-testid="country-isocode-input"
                    disabled={isCreating}
                    error={form.formState.errors.isoCode?.message}
                    {...field}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value
                          .toUpperCase()
                          .replace(/[^A-Z]/g, '')
                          .slice(0, 2),
                      );
                    }}
                    maxLength={2}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="currencyCode"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <SelectInput
                  label="Moneda base"
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isCreating}
                  placeholder="Seleccionar moneda"
                  error={form.formState.errors.currencyCode?.message}
                  data-testid="country-currency-select"
                >
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.name} ({currency.symbol})
                    </SelectItem>
                  ))}
                </SelectInput>
              </FormControl>
            </FormItem>
          )}
        />
      </ModalForm>

      <ConfirmDialog
        open={!!pendingValues}
        onOpenChange={(v) => !isCreating && !v && setPendingValues(null)}
        title="Confirmar registro"
        description={
          <span>
            Se registrará el territorio{' '}
            <span className="font-medium text-light">{pendingValues?.officialName}</span>
            {pendingValues?.level === TerritorialLevel.Pais && (
              <>
                {' '}
                (ISO: <span className="font-medium text-light">{pendingValues?.isoCode}</span>)
              </>
            )}{' '}
            como{' '}
            <span className="font-medium text-light">
              {LEVEL_LABELS[pendingValues?.level ?? ''] ?? pendingValues?.level}
            </span>
            .
          </span>
        }
        onConfirm={handleConfirm}
        confirmLabel="Confirmar"
        confirmLoadingLabel="Guardando..."
        isPending={isCreating}
        onCancel={() => setPendingValues(null)}
      />
    </>
  );
}
